/**
 * Reverse geocoding — turning a point on the map into استان / شهر / نشانی.
 *
 * One seam, on purpose. The clinic panel is used from inside Iran, and the
 * public OSM geocoder is the WRONG long-term answer for Persian addresses: it
 * is rate-limited, its usage policy is not written for a product, and its
 * Persian coverage of small streets is thin. An Iranian provider (Neshan,
 * map.ir) is the eventual answer, and both need an API key.
 *
 * So callers get `reverseGeocode()` and never learn who answered it. Swapping
 * the provider is a change in this file plus one deployment variable.
 *
 * **A keyed provider must not be called from here.** The bundle is public and
 * `window.__ENV__` is readable in devtools, so the day a key is involved the
 * call moves behind the clinic BFF and this module posts to it instead — which
 * is why the provider is chosen by name rather than by URL.
 *
 * What comes back is normalised through `iranGeo`, so a clinic located on the
 * map and a clinic picked from the dropdowns store the SAME spelling — see the
 * «استان تهران» case in `normalizePlace`.
 *
 * Except `abroad`: a clinic outside Iran has no dropdown to agree with, so its
 * names come back as the geocoder spells them, in the panel's `language`.
 */

import { matchCity, matchProvince, provinceOfCity } from '../constants/iranGeo';
import { GEOCODER, GEOCODER_URL } from './runtimeEnv';

/** Give up rather than leave «در حال دریافت آدرس...» on screen forever. */
const TIMEOUT_MS = 12_000;

/** Reasons a caller may want to phrase differently. Never user-facing text. */
export const GEOCODE_ERRORS = {
  unsupported: 'unsupported',
  network: 'network',
  timeout: 'timeout',
  empty: 'empty',
};

export class GeocodeError extends Error {
  constructor(reason) {
    super(`reverse geocode failed: ${reason}`);
    this.reason = reason;
  }
}

/**
 * Nominatim's `address` object → our shape.
 *
 * `state` is the province and arrives as «استان تهران»; `city`/`town`/`village`
 * are one field in three names depending on how big the place is; and the road
 * parts are joined with «، » because that is a Persian postal address, not a
 * list in a sentence — `formatList` would render an English "and" around
 * Persian data.
 */
function fromNominatim(data, { abroad = false, language = 'fa' } = {}) {
  const addr = data?.address || {};
  const rawProvince = addr.state || addr.province || addr.county || '';
  const rawCity = addr.city || addr.town || addr.village || addr.municipality || addr.county || '';
  const province = abroad ? rawProvince : (matchProvince(rawProvince) || provinceOfCity(rawCity) || rawProvince || '');
  const city = abroad ? rawCity : (matchCity(province, rawCity) || rawCity || '');
  const address = [addr.road, addr.neighbourhood, addr.suburb, addr.house_number]
    .filter(Boolean)
    // i18n-exempt: a postal address separator, not UI copy — «، » in Persian and Arabic.
    .join(abroad && language === 'en' ? ', ' : '، ');
  return { province, city, address };
}

async function nominatim({ latitude, longitude, signal, abroad, language }) {
  const base = GEOCODER_URL || 'https://nominatim.openstreetmap.org/reverse';
  const url = `${base}?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}&format=json&zoom=18&addressdetails=1`;
  const lang = abroad && language ? language : 'fa';
  const res = await fetch(url, { signal, headers: { 'Accept-Language': lang } });
  if (!res.ok) throw new GeocodeError(GEOCODE_ERRORS.network);
  const data = await res.json();
  if (!data || data.error) throw new GeocodeError(GEOCODE_ERRORS.empty);
  return fromNominatim(data, { abroad, language: lang });
}

const PROVIDERS = { nominatim };

/**
 * `{latitude, longitude}` → `{province, city, address}`.
 *
 * Never throws for "the geocoder had nothing to say" in a way the caller must
 * special-case: an unknown place comes back with empty strings, and only a real
 * failure (offline, blocked, timed out) rejects with a `GeocodeError`. The
 * coordinates are the part that matters and the caller already has them.
 */
export async function reverseGeocode({ latitude, longitude, abroad = false, language }) {
  const provider = PROVIDERS[GEOCODER] || nominatim;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), TIMEOUT_MS);
  try {
    return await provider({ latitude, longitude, abroad, language, signal: controller?.signal });
  } catch (err) {
    if (err instanceof GeocodeError) throw err;
    throw new GeocodeError(controller?.signal?.aborted ? GEOCODE_ERRORS.timeout : GEOCODE_ERRORS.network);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The browser's own location, as a promise.
 *
 * Split out from the geocoder because the two fail for unrelated reasons and
 * the UI says different things about them: a denied permission is the user's
 * decision and must leave the map usable, where a geocoder failure still leaves
 * a perfectly good pair of coordinates.
 */
export const GEO_ERRORS = { unsupported: 'unsupported', denied: 'denied', unavailable: 'unavailable', timeout: 'timeout' };

export function currentPosition({ timeout = 15_000 } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error(GEO_ERRORS.unsupported));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => {
        const byCode = { 1: GEO_ERRORS.denied, 2: GEO_ERRORS.unavailable, 3: GEO_ERRORS.timeout };
        reject(new Error(byCode[err?.code] || GEO_ERRORS.unavailable));
      },
      { enableHighAccuracy: true, timeout, maximumAge: 0 },
    );
  });
}
